import { useNotify } from "@/hooks/useNotify";
import { useNavigate } from "react-router-dom";
import { useRef, useEffect, useCallback, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { AuthStateType } from "./types/auth-state";
import { authConflictDetector } from "@/utils/authConflictDetector";
import { tabCoordinator } from "@/utils/tabCoordinator";
import { recordLoginTimestamp, isForcedReauthRequired } from "@/utils/sessionTimeout";
import { toast } from "sonner";

const IMPERSONATION_TIMEOUT_MS = 5 * 60 * 1000; // 5 minutes

export function useAuthOperations(authState: AuthStateType) {
  const notify = useNotify();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const inactivityTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const inactivityCleanupRef = useRef<(() => void) | null>(null);
  const countdownIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const lastActivityRef = useRef<number>(Date.now());
  const isEndingImpersonationRef = useRef<boolean>(false);
  const originalUserRef = useRef<typeof authState.originalUser>(authState.originalUser);
  const isImpersonatingRef = useRef<boolean>(authState.isImpersonating ?? false);
  const [impersonationTimeRemaining, setImpersonationTimeRemaining] = useState<number | null>(null);
  const {
    setSession,
    setUser,
    setProfile,
    setIsLoading,
    setIsSuperuser,
    setIsAdmin,
    setOriginalUser,
    setIsImpersonating,
    user,
    session,
    originalUser
  } = authState;

  const clearInactivityTimer = useCallback(() => {
    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current);
      inactivityTimerRef.current = null;
    }
    if (inactivityCleanupRef.current) {
      inactivityCleanupRef.current();
      inactivityCleanupRef.current = null;
    }
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
    }
    setImpersonationTimeRemaining(null);
  }, []);

  const endImpersonationDueToInactivity = useCallback(async () => {
    if (isEndingImpersonationRef.current) return;
    isEndingImpersonationRef.current = true;
    const currentOriginalUser = originalUserRef.current;
    const currentIsImpersonating = isImpersonatingRef.current;
    if (!currentIsImpersonating || !currentOriginalUser) {
      isEndingImpersonationRef.current = false;
      return;
    }
    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current);
      inactivityTimerRef.current = null;
    }
    if (inactivityCleanupRef.current) {
      inactivityCleanupRef.current();
      inactivityCleanupRef.current = null;
    }
    try {
      await supabase.rpc('end_impersonation');
      const { data: sessionData } = await supabase.auth.getSession();
      setIsImpersonating(false);
      setOriginalUser(null);
      setUser(currentOriginalUser);
      setSession(sessionData?.session ?? null);
      sessionStorage.removeItem('impersonation');
      const { data } = await supabase
        .from("profiles")
        .select("*")
        .eq("id", currentOriginalUser.id)
        .single();
      if (data) {
        setProfile({
          id: data.id || "",
          user_id: data.id || "",
          email: null,
          first_name: data.first_name || null,
          last_name: data.last_name || null,
          personal_phone_number: data.personal_phone_number || null,
          profile_photo_url: data.profile_photo_url || null,
          company_position: data.company_position || null,
          profile_completed: data.profile_completed || false,
          company_id: data.company_id || null,
          status: data.status || "active"
        });
      }
      setIsAdmin(false);
      setIsSuperuser(true);
      toast.warning("Impersonation ended due to inactivity");
      try { window.close(); } catch (e) { /* ignore */ }
      setTimeout(() => {
        if (!window.closed) {
          window.location.href = 'https://admin.doyencegroup.com';
        }
      }, 500);
    } catch (error) {
      console.error("Error ending impersonation due to inactivity:", error);
    } finally {
      isEndingImpersonationRef.current = false;
    }
  }, [setIsImpersonating, setOriginalUser, setUser, setSession, setProfile, setIsAdmin, setIsSuperuser]);

  const startInactivityTimer = useCallback(() => {
    clearInactivityTimer();
    const resetTimer = () => {
      if (inactivityTimerRef.current) {
        clearTimeout(inactivityTimerRef.current);
      }
      inactivityTimerRef.current = setTimeout(() => {
        endImpersonationDueToInactivity();
      }, IMPERSONATION_TIMEOUT_MS);
    };
    const onActivity = () => {
      lastActivityRef.current = Date.now();
      resetTimer();
    };
    document.addEventListener('click', onActivity);
    document.addEventListener('keydown', onActivity);
    document.addEventListener('scroll', onActivity);
    inactivityCleanupRef.current = () => {
      document.removeEventListener('click', onActivity);
      document.removeEventListener('keydown', onActivity);
      document.removeEventListener('scroll', onActivity);
    };
    lastActivityRef.current = Date.now();
    resetTimer();

    countdownIntervalRef.current = setInterval(() => {
      const elapsed = Date.now() - lastActivityRef.current;
      const remaining = IMPERSONATION_TIMEOUT_MS - elapsed;
      const clamped = Math.max(0, remaining);
      setImpersonationTimeRemaining(clamped);
      if (clamped === 0) {
        if (countdownIntervalRef.current) {
          clearInterval(countdownIntervalRef.current);
          countdownIntervalRef.current = null;
        }
        endImpersonationDueToInactivity();
      }
    }, 1000);
  }, [clearInactivityTimer, endImpersonationDueToInactivity]);

  useEffect(() => {
    return () => clearInactivityTimer();
  }, [clearInactivityTimer]);

  useEffect(() => {
    originalUserRef.current = originalUser;
  }, [originalUser]);

  useEffect(() => {
    isImpersonatingRef.current = authState.isImpersonating ?? false;
  }, [authState.isImpersonating]);

  const restoreImpersonation = async () => {
    try {
      const { data: { session: currentSession } } = await supabase.auth.getSession();
      const currentUser = currentSession?.user;
      if (!currentUser) return;

      const { data: activeSession } = await supabase
        .from('impersonation_sessions')
        .select('target_user_id, started_at')
        .eq('admin_id', currentUser.id)
        .single();

      if (!activeSession) return;

      console.log('[Auth] Restoring impersonation session from DB:', activeSession);

      // Re-establish the server-side impersonation context for RLS
      const { error: startError } = await supabase.rpc(
        'start_impersonation',
        { target_user_id: activeSession.target_user_id }
      );
      if (startError) {
        console.error('[Auth] Failed to restore impersonation context:', startError);
        return;
      }

      const { data: effectiveCompanyId } = await supabase.rpc('get_effective_user_company_id');

      const { data: profileData, error: profileError } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', activeSession.target_user_id)
        .single();

      if (profileError || !profileData) {
        console.error('[Auth] Failed to fetch impersonated profile:', profileError);
        return;
      }

      setOriginalUser(currentUser);
      setIsImpersonating(true);
      setUser({
        ...currentUser,
        id: activeSession.target_user_id,
        impersonated: true
      } as any);
      setProfile({
        id: profileData.id || "",
        user_id: profileData.id || "",
        email: null,
        first_name: profileData.first_name || null,
        last_name: profileData.last_name || null,
        personal_phone_number: profileData.personal_phone_number || null,
        profile_photo_url: profileData.profile_photo_url || null,
        company_position: profileData.company_position || null,
        profile_completed: profileData.profile_completed || false,
        company_id: effectiveCompanyId || profileData.company_id || null,
        status: profileData.status || "active"
      });

      sessionStorage.setItem('impersonation', JSON.stringify({
        isImpersonating: true,
        originalUserId: currentUser.id,
        impersonatedUserId: activeSession.target_user_id
      }));

      queryClient.clear();
      startInactivityTimer();
      toast.info("Impersonation session restored");
    } catch (error) {
      console.error('[Auth] Error restoring impersonation:', error);
    }
  };

  const signInWithEmailAndPassword = async (email: string, password: string) => {
    try {
      setIsLoading(true);
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        // Check if it's invalid credentials and might be a deactivated user
        if (error.message?.includes('Invalid login credentials')) {
          try {
            // Check if user exists but is deactivated
            const { data: userCheck } = await supabase
              .from('profiles')
              .select(`
                id,
                company_members!inner(status, deactivated_at, deactivation_reason)
              `)
              .ilike('id', `%${email}%`) // This won't work with email, need auth.users
              .single();
            
            // Since we can't easily query auth.users, we'll let the SecurityGuard handle this
            // Just throw the original error for now
            throw error;
          } catch {
            // If check fails, throw original error
            throw error;
          }
        } else {
          throw error;
        }
      }

      // Record login timestamp for session age tracking
      recordLoginTimestamp();

      notify.success("Welcome back!");
    } catch (error: any) {
      console.error("Login error:", error);
      throw error; // Let the calling component handle the error
    } finally {
      setIsLoading(false);
    }
  };

  const signUp = async (email: string, password: string, metadata?: any) => {
    try {
      setIsLoading(true);
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: metadata,
          emailRedirectTo: window.location.origin + '/verify',
        },
      });

      if (error) throw error;

      notify.success("Please check your email for verification.");

      navigate('/verify', { 
        state: { email },
        replace: true 
      });
    } catch (error: any) {
      console.error("Registration error:", error);
      notify.error(error.message || "Registration failed. Please try again.");
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const verifyOtp = async (email: string, token: string) => {
    try {
      setIsLoading(true);
      const { data, error } = await supabase.auth.verifyOtp({
        email,
        token,
        type: 'signup'
      });
      
      if (error) throw error;
      
      notify.success("Your email has been successfully verified.");
      
      if (data.user) {
        navigate("/profile-setup", { replace: true });
      }
    } catch (error: any) {
      console.error("Verification error:", error);
      notify.error(error.message || "Please try again with a valid code.");
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const resendVerificationEmail = async (email: string) => {
    try {
      setIsLoading(true);
      const { error } = await supabase.auth.resend({
        type: 'signup',
        email,
        options: {
          emailRedirectTo: window.location.origin + '/verify',
        }
      });

      if (error) throw error;

      notify.success("Please check your email for the verification link.");
    } catch (error: any) {
      console.error("Resend error:", error);
      notify.error(error.message || "Failed to resend verification email. Please try again.");
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const resetPassword = async (email: string) => {
    try {
      setIsLoading(true);
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: window.location.origin + '/reset-password',
      });

      if (error) throw error;

      notify.success("Please check your email for the password reset link.");
      
      return true;
    } catch (error: any) {
      console.error("Password reset error:", error);
      notify.error(error.message || "Failed to send password reset email. Please try again.");
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const updatePassword = async (newPassword: string): Promise<boolean> => {
    try {
      setIsLoading(true);
      const { error } = await supabase.auth.updateUser({ password: newPassword });

      if (error) {
        // AuthRetryableFetchError means the connection dropped mid-response.
        // The password update may have succeeded on the server even though
        // the client never received the success response. This happens because
        // Supabase rotates session tokens during password update which can
        // cause the cookie write to drop the connection on doyencegroup.com domains.
        if (error.name === 'AuthRetryableFetchError' || error.message?.includes('Failed to fetch')) {
          console.warn('[Auth] updatePassword: retryable fetch error - password may have been updated successfully, navigating to login');
          notify.success("Password updated. Please sign in with your new password.");
          await supabase.auth.signOut();
          navigate("/login", { replace: true });
          return true;
        }
        throw error;
      }

      notify.success("Your password has been successfully updated.");
      await supabase.auth.signOut();
      navigate("/login", { replace: true });
      return true;
    } catch (error: any) {
      console.error("Password update error:", error);
      notify.error(error.message || "Failed to update password. Please try again.");
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const impersonateUser = async (userId: string) => {
    try {
      setIsLoading(true);
      
      if (!authState.isImpersonating) {
        setOriginalUser(user);
      }
      
      // Log the impersonation start (function will be available after types regeneration)
      console.log('Admin impersonation started:', { userId, adminId: user?.id });
      
      const { error: impersonateError } = await supabase.rpc('admin_impersonate_user', {
        p_user_id: userId
      });
      
      if (impersonateError) {
        throw impersonateError;
      }
      
      // Set server-side impersonation context for RLS policies
      const { error: startError } = await supabase.rpc(
        'start_impersonation',
        { target_user_id: userId }
      );
      if (startError) throw startError;
      
      const { data: effectiveCompanyId, error: companyError } = await supabase.rpc(
        'get_effective_user_company_id'
      );
      
      if (companyError) {
        throw companyError;
      }
      
      const { data: profileData, error: profileError } = await supabase
        .from("profiles")
        .select("*")
        .eq("id", userId)
        .single();
        
      if (profileError) {
        throw profileError;
      }
      
      setIsImpersonating(true);
      
      // Persist impersonation state to sessionStorage
      sessionStorage.setItem('impersonation', JSON.stringify({
        isImpersonating: true,
        originalUserId: originalUser?.id || user?.id,
        impersonatedUserId: userId
      }));
      
      const impersonatedUser = {
        ...user,
        id: userId,
        impersonated: true
      };
      
      setUser(impersonatedUser);
      
      const profileData2 = {
        id: profileData.id || "",
        user_id: profileData.id || "",
        email: null, // Email no longer stored in profiles
        first_name: profileData.first_name || null,
        last_name: profileData.last_name || null,
        personal_phone_number: profileData.personal_phone_number || null,
        profile_photo_url: profileData.profile_photo_url || null,
        company_position: profileData.company_position || null,
        profile_completed: profileData.profile_completed || false,
        company_id: effectiveCompanyId || profileData.company_id || null,
        status: profileData.status || "active"
      };
      
      setProfile(profileData2);
      
      notify.success(`You are now viewing as ${profileData2.first_name || ''} ${profileData2.last_name || ''}`);
      
      queryClient.clear();
      navigate("/dashboard");
      
      // Start inactivity timer
      startInactivityTimer();
      
    } catch (error: any) {
      console.error("Impersonation error:", error);
      notify.error(error.message || "Could not impersonate user");
    } finally {
      setIsLoading(false);
    }
  };

  const endImpersonation = async () => {
    if (!authState.isImpersonating || !originalUser) {
      return;
    }
    
    // Clear inactivity timer immediately
    clearInactivityTimer();
    
    try {
      setIsLoading(true);
      
      const { error: endImpersonationError } = await supabase.rpc('end_impersonation');
      
      if (endImpersonationError) {
        throw endImpersonationError;
      }
      
      const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
      
      if (sessionError) {
        throw sessionError;
      }
      
      // Fetch admin profile BEFORE clearing state to prevent flash
      const { data, error } = await supabase
        .from("profiles")
        .select("*")
        .eq("id", originalUser.id)
        .single();
        
      if (error) {
        throw error;
      }
      
      const profileData = {
        id: data.id || "",
        user_id: data.id || "",
        email: null,
        first_name: data.first_name || null,
        last_name: data.last_name || null,
        personal_phone_number: data.personal_phone_number || null,
        profile_photo_url: data.profile_photo_url || null,
        company_position: data.company_position || null,
        profile_completed: data.profile_completed || false,
        company_id: data.company_id || null,
        status: data.status || "active"
      };
      
      // Clear all state atomically now that profile is ready
      setIsImpersonating(false);
      setOriginalUser(null);
      setUser(originalUser);
      setSession(sessionData.session);
      setProfile(profileData);
      setIsAdmin(false);
      setIsSuperuser(true);
      
      // Clear sessionStorage
      sessionStorage.removeItem('impersonation');
      
      notify.success("You've returned to your admin account");
      
      queryClient.clear();
      try { window.close(); } catch (e) { /* ignore */ }
      setTimeout(() => {
        if (!window.closed) {
          window.location.href = 'https://admin.doyencegroup.com';
        }
      }, 300);
      
    } catch (error: any) {
      console.error("Error ending impersonation:", error);
      notify.error(error.message || "Could not end impersonation session");
    } finally {
      setIsLoading(false);
    }
  };

  const signOut = async (force: boolean = false, reason?: string) => {
    try {
      // Check for potential auth conflicts before signing out
      const conflict = authConflictDetector.detectConflictBeforeSignOut(reason);
      
      if (conflict && !force) {
        authConflictDetector.logConflict(conflict, 'prevented');
        
        // If this is a high severity conflict, prevent the sign-out
        if (conflict.severity === 'high') {
          console.warn('[Auth] Sign-out prevented due to conflict:', conflict);
          notify.error("Please close other tabs and try again.");
          return;
        }
      }
      
      if (conflict) {
        authConflictDetector.logConflict(conflict, 'allowed');
      }
      
      setIsLoading(true);
      
      // Enhanced logging for sign-out operations
      const tabInfo = {
        tabId: tabCoordinator.getCurrentTabId(),
        shouldHandleAuth: tabCoordinator.shouldHandleAuth(),
        activeTabs: tabCoordinator.getActiveTabsInfo().length,
        isInJoinFlow: tabCoordinator.isInJoinFlowContext()
      };
      
      console.log('[Auth] Sign-out initiated', {
        force,
        reason,
        conflict: conflict?.conflictType || 'none',
        ...tabInfo,
        timestamp: new Date().toISOString()
      });
      
      if (authState.isImpersonating && originalUser) {
        await endImpersonation();
        return;
      }
      
      const { error } = await supabase.auth.signOut({ __allowOnRestricted: true } as any);

      if (error) throw error;

      notify.success("You have been successfully logged out.");

      navigate("/login", { replace: true });
    } catch (error: any) {
      console.error("Sign out error:", error);
      notify.error(error.message || "Error signing out. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  return {
    signInWithEmailAndPassword,
    signUp,
    signOut,
    verifyOtp,
    resendVerificationEmail,
    resetPassword,
    updatePassword,
    impersonateUser,
    endImpersonation,
    restoreImpersonation,
    impersonationTimeRemaining,
  };
}
